import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Voicemail } from '@material-ui/icons';
import * as actions from '~/store/actions';
import * as lib from '~/dependencies';
import { useDtmfSound, useTryToCall } from '~/hooks';
import { countryCodeAndFlagSelected, isExtension } from '~/helpers';
import * as NuacomComponent from '~/components';
import ContactList from './components/ContactList';
import {
  Container,
  SearchContactsList,
  VoiceMessageTab,
  VoiceButton,
  Badge,
} from './styles';

export default function DialPad(props) {
  // STATES
  const [countryCode, setCountryCode] = useState({});
  const [cursor, setCursor] = useState(0);

  const numberTo = useSelector(state => state.system.numberToTransfer || '');

  // SELECTORS
  const userCountryValue = useSelector(state => state.auth?.userCountry);
  const userTempCountryCode = useSelector(
    state => state.system?.temporaryISOCode
  );
  const callerIdCountryCode = useSelector(
    state => state.system?.callerIdCountryCode
  );

  const callCurrent = useSelector(state => state.callCurrent?.data?.length);

  const voiceMessages = useSelector(state => state.phone?.voiceMessages);

  // HOOKSf
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();
  const setSoundDtmf = useDtmfSound();
  const tryToCall = useTryToCall();

  // CONSTs
  const totalCalls = callCurrent || 0;
  const { heard, unheard } = voiceMessages;
  const userCountry = numberTo ? userTempCountryCode : userCountryValue;

  function inputNumberHandler(number, action) {
    const numberFormated = String(number).replace(/[^0-9*#+]+/, '');

    if (action !== 'erase') {
      setSoundDtmf(numberFormated[numberFormated.length - 1]);
    }

    dispatch(actions.numberToTransfer(numberFormated));
  }

  function resetNumber() {
    dispatch(actions.numberToTransfer());
  }

  function backSpaceNumber() {
    if (numberTo.length > 0) {
      inputNumberHandler(numberTo.substring(0, numberTo.length - 1), 'erase');
    } else {
      resetNumber();
    }
  }

  function cutNumber() {
    // web api is delaying to execute the cut operation
    setTimeout(() => {
      resetNumber();
    }, 100);
  }

  function getCursorPosition(number) {
    return cursor <= number.length && number.length >= numberTo.length
      ? number.length
      : cursor - 1;
  }

  function insertByPosition(number, type) {
    if (number !== '' && type === 'keypad') {
      const adjustCursor = cursor >= numberTo.length ? numberTo.length : cursor;

      setCursor(cursor + 1);

      const values =
        numberTo.length === 0
          ? number
          : numberTo.slice(0, adjustCursor) +
            number +
            numberTo.slice(adjustCursor);
      inputNumberHandler(values);
    } else if (number !== '') {
      const adjustCursor = getCursorPosition(number);
      inputNumberHandler(number);
      setCursor(adjustCursor);
    } else {
      backSpaceNumber();
    }
  }

  function handleKeyPress(event) {
    event.preventDefault();
    if (event.key === 'Enter') {
      if (totalCalls < 2) {
        resetNumber();
        if (!isExtension(numberTo) && !numberTo?.startsWith('*')) {
          tryToCall(`00${countryCode && countryCode.callingCode}${numberTo}`);
        } else {
          tryToCall(numberTo);
        }
      }
    }
  }

  function call() {
    if (totalCalls < 2) {
      if (numberTo?.length) {
        resetNumber();
        if (!isExtension(numberTo) && !numberTo.startsWith('*')) {
          tryToCall(`00${countryCode && countryCode.callingCode}${numberTo}`);
        } else {
          tryToCall(numberTo);
        }
      }
    }
  }

  function handleCursor(value) {
    setCursor(value);
  }

  function handleCountryCode(cc) {
    if (cc) {
      setCountryCode(cc);
      dispatch(actions.addTemporaryCountryCode(cc.callingCode, cc.code));
    }
  }

  // EFFECTS
  useEffect(() => {
    if (userCountry) {
      const flagSelected = countryCodeAndFlagSelected(userCountry);
      if (flagSelected) {
        const { callingCode, code } = flagSelected;
        setCountryCode({
          callingCode: callingCode || callerIdCountryCode,
          code,
        });
        dispatch(actions.addTemporaryCountryCode(callingCode, code));
      }
    }
  }, [callerIdCountryCode, userCountry, setCountryCode, dispatch]);

  useEffect(() => {
    dispatch(actions.getConnectedIntegrations());
  }, [dispatch]);

  return (
    <Container>
      <NuacomComponent.Header center {...props}>
        <NuacomComponent.CallerIdSelector />
      </NuacomComponent.Header>

      {unheard > 0 && (
        <VoiceMessageTab>
          <VoiceButton
            name="dialpad-mail-btn"
            title={t('screens.dialpad.voicemail', { unheard, heard })}
            onClick={() => tryToCall('*71')}>
            <Badge>
              <center>{unheard >= 10 ? '+9' : unheard}</center>
            </Badge>
            <Voicemail className="voice-mail-btn" />
          </VoiceButton>
        </VoiceMessageTab>
      )}

      <SearchContactsList>
        <ContactList
          setCountryCode={handleCountryCode}
          inputNumberHandler={inputNumberHandler}
          withCall={totalCalls > 0}
        />
      </SearchContactsList>

      <NuacomComponent.NumberField
        countryCode={countryCode}
        setCountryCode={handleCountryCode}
        value={numberTo}
        changed={insertByPosition}
        backSpace={backSpaceNumber}
        cut={cutNumber}
        callAction={call}
        cursor={handleCursor}
      />

      <NuacomComponent.DialNumber
        insertByPosition={insertByPosition}
        handleKeyPress={handleKeyPress}
      />
    </Container>
  );
}
